/**
 * VariantButton Module
 * Handles color variant selection and display logic
 */
class VariantButtonManager {
    constructor() {
        this.fallbackColors = [];
        this.pandaColors = [];
        this.initialized = false;
    }

    /**
     * Initialize the variant button manager
     * @param {Array} metafieldColors - Colors from shop metafield
     * @param {Array} fallbackColors - Fallback color database
     */
    init(metafieldColors = [], fallbackColors = []) {
        if (this.initialized) {
            console.log('[VariantButton] Already initialized, skipping');
            return;
        }

        console.log('[VariantButton] Initializing...');
        console.log('[VariantButton] Metafield colors:', metafieldColors.length);
        console.log('[VariantButton] Fallback colors:', fallbackColors.length);

        this.fallbackColors = fallbackColors;
        this.pandaColors = metafieldColors;
        this.initialized = true;

        this.handleWhiteColors();
        this.setupColorButtons();

        console.log('[VariantButton] Initialization complete');
    }

    /**
     * Toggle variant details section
     * @param {HTMLElement} variantDiv - The variant preview section element
     */
    toggleVariantDetails(variantDiv) {
        const detailsSection = variantDiv.parentElement.querySelector('.VariantDetailsSection');
        if (detailsSection) {
            const isVisible = detailsSection.style.display === 'block';
            detailsSection.style.display = isVisible ? 'none' : 'block';
        }
    }

    /**
     * Change variant selection
     * @param {HTMLElement} variantButton - The clicked variant button
     * @param {string} variantValue - The variant value to select
     * @param {string|null} colorCode - The color code (if color variant)
     */
    changeVariant(variantButton, variantValue, colorCode) {
        const variantDiv = variantButton.closest('.VariantSelectDiv');
        if (!variantDiv) return;

        const variantSelect = variantDiv.querySelector('.VariantSelect');
        if (!variantSelect) return;

        variantSelect.value = variantValue;
        variantSelect.dispatchEvent(new Event('change'));

        // Check if product has media
        const hasProductMedia = variantButton.dataset.hasProductMedia === 'true';
        if (!hasProductMedia) {
            window.location.reload();
            return;
        }

        // Update color preview if it's a color variant
        if (colorCode !== undefined) {
            this.updateColorPreview(variantButton, colorCode);
        } else {
            this.updateTextVariantPreview(variantButton);
        }

        // Update display value
        this.updateDisplayValue(variantButton, variantValue);
        this.handleWhiteColors();
    }

    /**
     * Update color preview
     */
    updateColorPreview(variantButton, colorCode) {
        const variantDiv = variantButton.closest('.VariantDiv');
        if (!variantDiv) return;

        const previewDiv = variantDiv.querySelector('.ColorVariantDivPreview');
        const previewButton = previewDiv?.querySelector('.ColorVariantButtonPreview');

        if (colorCode) {
            // Valid color code
            if (previewDiv) {
                previewDiv.style.border = `2px solid ${colorCode}`;
            }
            if (previewButton) {
                previewButton.style.background = colorCode;
            }

            variantButton.style.border = `2px solid ${colorCode}`;
            const siblings = variantButton.parentElement.querySelectorAll('.ColorVariantDiv');
            siblings.forEach(sibling => {
                if (sibling !== variantButton) {
                    sibling.style.border = 'none';
                }
            });
        } else if (colorCode === null) {
            // Text-based color fallback
            if (previewDiv) {
                previewDiv.style.background = '#d9d9d9';
                previewDiv.style.border = '2px solid #d9d9d9';
            }

            variantButton.style.background = '#d9d9d9';
            variantButton.style.border = '2px solid #d9d9d9';

            const siblings = variantButton.parentElement.querySelectorAll('.ColorVariantDiv');
            siblings.forEach(sibling => {
                if (sibling !== variantButton) {
                    sibling.style.background = '';
                    sibling.style.border = '1px solid #d9d9d9';
                }
            });
        }
    }

    /**
     * Update text variant preview
     */
    updateTextVariantPreview(variantButton) {
        variantButton.classList.add('DefaultVariantButtonActivated');

        const siblings = variantButton.parentElement.querySelectorAll('.DefaultVariantButton');
        siblings.forEach(sibling => {
            if (sibling !== variantButton) {
                sibling.classList.remove('DefaultVariantButtonActivated');
            }
        });
    }

    /**
     * Update display value in preview section
     */
    updateDisplayValue(variantButton, variantValue) {
        const variantDiv = variantButton.closest('.VariantDiv');
        if (!variantDiv) return;

        const displayElement = variantDiv.querySelector('.SelectedDefaultValue');
        if (!displayElement) return;

        const productTitle = variantButton.dataset.productTitle || '';
        const isAKSProduct = variantButton.dataset.isAksProduct === 'true';
        const optionNameDowncase = variantButton.dataset.optionName || '';

        let displayValue = variantValue;

        // Remove product title parts from display value
        if (!isAKSProduct || optionNameDowncase !== 'modell') {
            const productTitleParts = productTitle.split(' ');
            for (const part of productTitleParts) {
                if (variantValue.includes(part)) {
                    displayValue = displayValue.replace(part, '');
                }
            }

            if (displayValue.trim() === '') {
                displayValue = variantValue;
            }
        }

        displayElement.textContent = displayValue;
    }

    /**
     * Handle white color borders
     */
    handleWhiteColors() {
        const colorButtons = document.querySelectorAll('.ColorVariantButton');
        colorButtons.forEach(button => {
            button.style.border = '1px solid #e6e6e6';
        });

        const colorDivs = document.querySelectorAll('.ColorVariantDiv');
        colorDivs.forEach(div => {
            const borderColor = window.getComputedStyle(div).borderColor;
            if (borderColor === 'rgb(255, 255, 255)') {
                div.style.border = '2px solid #e6e6e6';
            }
        });
    }

    /**
     * Find color code from color name
     * @param {string} colorName - The color name to look up
     * @returns {string|null} The color code or null if not found
     */
    findColorCode(colorName) {
        const normalizedName = colorName.toLowerCase().trim().replace(/\s+/g, ' ');

        // Try metafield colors first
        let colorInfo = this.pandaColors.find(color => {
            const metaName = (color.Name || '').toLowerCase().trim().replace(/\s+/g, ' ');
            return metaName === normalizedName;
        });

        // Fallback to hardcoded colors
        if (!colorInfo) {
            colorInfo = this.fallbackColors.find(color => {
                const fallbackName = color.name.toLowerCase().trim().replace(/\s+/g, ' ');
                return fallbackName === normalizedName;
            });
        }

        if (colorInfo) {
            return colorInfo.Colorcode || colorInfo.colorcode;
        }

        console.warn(`Color code not found for: "${colorName}" (normalized: "${normalizedName}")`);
        return null;
    }

    /**
     * Setup color buttons
     */
    setupColorButtons() {
        const colorVariantDivs = document.querySelectorAll('.ColorVariantDiv');

        colorVariantDivs.forEach(colorVariantDiv => {
            const displayColorName = colorVariantDiv.getAttribute('colorName');
            const colorName = displayColorName.toLowerCase().trim();
            const selectedValue = colorVariantDiv.getAttribute('selectedValue').toLowerCase().trim();
            const colorCode = this.findColorCode(displayColorName);

            if (!colorCode) {
                this.setupTextFallback(colorVariantDiv, displayColorName, selectedValue, colorName);
                return;
            }

            // Setup color circle button
            if (colorVariantDiv.classList.contains('DetailsColorVariantDiv')) {
                colorVariantDiv.addEventListener('click', () => {
                    this.changeVariant(colorVariantDiv, displayColorName, colorCode);
                });
            }

            if (selectedValue === colorName) {
                colorVariantDiv.style.border = `2px solid ${colorCode}`;
            }

            const colorVariantButton = colorVariantDiv.querySelector('.ColorVariantButton');
            if (colorVariantButton) {
                colorVariantButton.style.background = colorCode;
            }
        });
    }

    /**
     * Setup text fallback for colors without code
     */
    setupTextFallback(colorVariantDiv, displayColorName, selectedValue, colorName) {
        const colorVariantButton = colorVariantDiv.querySelector('.ColorVariantButton');

        colorVariantDiv.style.width = 'auto';
        colorVariantDiv.style.minWidth = '30px';
        colorVariantDiv.style.padding = '0 8px';
        colorVariantDiv.style.borderRadius = '0.8rem';
        colorVariantDiv.style.border = '1px solid #d9d9d9';

        if (colorVariantButton) {
            colorVariantButton.style.display = 'none';
        }

        const textSpan = document.createElement('span');
        textSpan.textContent = displayColorName;
        textSpan.style.fontSize = '0.85em';
        textSpan.style.whiteSpace = 'nowrap';
        colorVariantDiv.appendChild(textSpan);

        if (colorVariantDiv.classList.contains('DetailsColorVariantDiv')) {
            colorVariantDiv.addEventListener('click', () => {
                this.changeVariant(colorVariantDiv, displayColorName, null);
            });
        }

        if (selectedValue === colorName) {
            colorVariantDiv.style.background = '#d9d9d9';
            colorVariantDiv.style.border = '2px solid #d9d9d9';
        }
    }
}

// Create global instance
console.log('[VariantButton] Creating global instance');
window.variantButtonManager = new VariantButtonManager();

// Global helper functions for backward compatibility
window.toggleVariantDetails = function(element) {
    window.variantButtonManager.toggleVariantDetails(element);
};

window.changeVariant = function(element, value, colorCode) {
    window.variantButtonManager.changeVariant(element, value, colorCode);
};

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {
    console.log('[VariantButton] DOM is loading, waiting for DOMContentLoaded event');
    document.addEventListener('DOMContentLoaded', () => {
        console.log('[VariantButton] DOMContentLoaded event fired');
        // Wait for color data to be loaded
        if (window.variantColorData) {
            console.log('[VariantButton] Color data found, initializing');
            window.variantButtonManager.init(
                window.variantColorData.metafieldColors,
                window.variantColorData.fallbackColors
            );
        } else {
            console.warn('[VariantButton] Color data not found on DOMContentLoaded');
        }
    });
} else {
    // DOM already loaded
    console.log('[VariantButton] DOM already loaded');
    if (window.variantColorData) {
        console.log('[VariantButton] Color data found, initializing');
        window.variantButtonManager.init(
            window.variantColorData.metafieldColors,
            window.variantColorData.fallbackColors
        );
    } else {
        console.warn('[VariantButton] Color data not found, waiting for manual initialization');
    }
}
